import React, { useEffect, useMemo, useState } from "react";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./AdminHeroSlides.css";

const emptySlide = {
  layout_type: "split_premium",
  layout_variant: "split-right-premium",
  brand_slug: "mayachitro",
  product_code: "",

  eyebrow_text: "Original Photography Marketplace",
  title_text: "Visual Stories from MayaChitro",
  subtitle_text:
    "Original photography downloads for homes, creators, publishers, and brands.",

  primary_button_text: "Shop Photography",
  primary_button_link: "/shop?brand=mayachitro",
  secondary_button_text: "View Collections",
  secondary_button_link: "/collections",
  third_button_text: "",
  third_button_link: "",

  main_image_path: "",
  main_image_fit: "cover",
  main_image_position: "center center",
  main_image_scale: 1,

  caption_enabled: true,
  caption_mode: "compact",
  caption_text: "",

  side_image_1: "",
  side_image_1_title: "",
  side_image_2: "",
  side_image_2_title: "",
  side_image_3: "",
  side_image_3_title: "",
  side_cards_count: 2,

  tag_1: "",
  tag_2: "",
  tag_3: "",

  trust_bar_enabled: true,
  theme_style: "dark",
  hero_size: "standard",

  auto_slide_seconds: 7,
  sort_order: 1,
  is_active: true,
};

function toAssetUrl(path) {
  if (!path || typeof path !== "string") return "";

  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) {
    return path;
  }

  if (path.startsWith("/uploads/public/")) {
    return buildPublicFileUrl(path);
  }

  if (path.startsWith("/uploads")) {
    return buildPublicFileUrl(path);
  }

  return path;
}

function dbBool(value) {
  return value === true || value === 1 || value === "1";
}

function normalizeForForm(slide) {
  return {
    ...emptySlide,
    ...slide,
    caption_enabled: dbBool(slide.caption_enabled),
    trust_bar_enabled: dbBool(slide.trust_bar_enabled),
    is_active: dbBool(slide.is_active),
    side_cards_count: Number(slide.side_cards_count ?? 2),
    main_image_scale: Number(slide.main_image_scale ?? 1),
    auto_slide_seconds: Number(slide.auto_slide_seconds ?? 7),
    sort_order: Number(slide.sort_order ?? 1),
  };
}

function Field({ label, children, hint }) {
  return (
    <label className="admin-hero-field">
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function PreviewImage({ path, label }) {
  const url = toAssetUrl(path);

  return (
    <div className="admin-hero-preview-image">
      {url ? (
        <img src={url} alt={label || "Hero preview"} />
      ) : (
        <div className="admin-hero-preview-fallback">No Image</div>
      )}
    </div>
  );
}

export default function AdminHeroSlides() {
  const [slides, setSlides] = useState([]);
  const [form, setForm] = useState(emptySlide);
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const activeSlides = useMemo(
    () => slides.filter((slide) => dbBool(slide.is_active)).length,
    [slides]
  );

  const loadSlides = async () => {
    try {
      setStatus("Loading hero slides...");

      const response = await fetch(buildApiUrl("/admin/hero-slides"), {
        credentials: "include",
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to load hero slides.");
      }

      setSlides(data.slides || []);
      setStatus("");
    } catch (error) {
      setStatus(error.message);
    }
  };

  useEffect(() => {
    loadSlides();
  }, []);

  const updateField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      ...emptySlide,
      sort_order: slides.length + 1,
    });
    setStatus("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editSlide = (slide) => {
    setEditingId(slide.id);
    setForm(normalizeForForm(slide));
    setStatus(`Editing slide #${slide.id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicateSlide = (slide) => {
    const copy = normalizeForForm(slide);

    setEditingId(null);
    setForm({
      ...copy,
      product_code: copy.product_code ? `${copy.product_code}-COPY` : "",
      title_text: `${copy.title_text || "Hero Slide"} Copy`,
      sort_order: slides.length + 1,
      is_active: false,
    });

    setStatus("Duplicated as a new inactive slide. Edit and save.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveSlide = async (event) => {
    event.preventDefault();

    try {
      setIsSaving(true);
      setStatus("Saving hero slide...");

      const url = editingId
        ? buildApiUrl(`/admin/hero-slides/${editingId}`)
        : buildApiUrl("/admin/hero-slides");

      const method = editingId ? "PUT" : "POST";

      const payload = {
        ...form,
        main_image_scale: Number(form.main_image_scale || 1),
        side_cards_count: Number(form.side_cards_count || 0),
        auto_slide_seconds: Number(form.auto_slide_seconds || 7),
        sort_order: Number(form.sort_order || 1),
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to save hero slide.");
      }

      await loadSlides();

      setStatus(
        editingId
          ? "Hero slide updated successfully."
          : "Hero slide created successfully."
      );

      setEditingId(null);
      setForm(emptySlide);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSlide = async (id) => {
    const confirmed = window.confirm("Delete this hero slide?");
    if (!confirmed) return;

    try {
      setStatus("Deleting hero slide...");

      const response = await fetch(buildApiUrl(`/admin/hero-slides/${id}`), {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to delete hero slide.");
      }

      await loadSlides();

      if (editingId === id) {
        setEditingId(null);
        setForm(emptySlide);
      }

      setStatus("Hero slide deleted successfully.");
    } catch (error) {
      setStatus(error.message);
    }
  };

  return (
    <main className="admin-hero-page">
      <section className="admin-hero-header">
        <div>
          <p>Homepage Control</p>
          <h1>Hero Slides</h1>
          <span>
            Manage homepage hero slides, layout, images, text, buttons, captions,
            active status, and slide order.
          </span>
        </div>

        <div className="admin-hero-header-actions">
          <strong>{slides.length} slides</strong>
          <small>{activeSlides} active</small>
          <button type="button" onClick={resetForm}>
            New Slide
          </button>
        </div>
      </section>

      {status ? <div className="admin-hero-status">{status}</div> : null}

      <section className="admin-hero-list priority-list">
        <div className="admin-hero-list-heading">
          <div>
            <p>Current Slides</p>
            <h2>Homepage Hero Slides</h2>
          </div>

          <button type="button" onClick={loadSlides}>
            Refresh
          </button>
        </div>

        {slides.length === 0 ? (
          <div className="admin-hero-empty">No hero slides found.</div>
        ) : (
          slides.map((slide) => (
            <article
              key={slide.id}
              className={`admin-hero-slide-card ${
                dbBool(slide.is_active) ? "" : "is-inactive"
              }`}
            >
              <PreviewImage path={slide.main_image_path} label={slide.title_text} />

              <div className="admin-hero-slide-info">
                <strong>
                  #{slide.id} — {slide.title_text || "Untitled Slide"}
                </strong>

                <span>
                  {slide.layout_type} · {slide.layout_variant || "default"} ·
                  order {slide.sort_order} ·{" "}
                  {dbBool(slide.is_active) ? "Active" : "Hidden"}
                </span>

                <small>{slide.main_image_path}</small>
              </div>

              <div className="admin-hero-actions">
                <button type="button" onClick={() => editSlide(slide)}>
                  Edit
                </button>

                <button type="button" onClick={() => duplicateSlide(slide)}>
                  Duplicate
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={() => deleteSlide(slide.id)}
                >
                  Delete
                </button>
              </div>
            </article>
          ))
        )}
      </section>

      <form className="admin-hero-form" onSubmit={saveSlide}>
        <div className="admin-hero-form-top">
          <div>
            <p>{editingId ? "Editing" : "Create / Edit"}</p>
            <h2>{editingId ? `Hero Slide #${editingId}` : "Hero Slide Settings"}</h2>
          </div>

          <div className="admin-hero-form-actions">
            {editingId ? (
              <button type="button" className="ghost" onClick={resetForm}>
                Cancel Edit
              </button>
            ) : null}

            <button type="submit" disabled={isSaving}>
              {isSaving
                ? "Saving..."
                : editingId
                ? "Update Slide"
                : "Create Slide"}
            </button>
          </div>
        </div>

        <div className="admin-hero-two-column">
          <section className="admin-hero-card">
            <h3>Layout Settings</h3>

            <div className="admin-hero-grid">
              <Field label="Layout Type">
                <select
                  value={form.layout_type}
                  onChange={(e) => updateField("layout_type", e.target.value)}
                >
                  <option value="split_premium">Split Premium</option>
                  <option value="cinematic_banner">Cinematic Banner</option>
                  <option value="wall_art_light">Wall Art Light</option>
                </select>
              </Field>

              <Field label="Layout Variant">
                <select
                  value={form.layout_variant || "default"}
                  onChange={(e) => updateField("layout_variant", e.target.value)}
                >
                  <option value="default">Default</option>
                  <option value="split-right-premium">Split Right Premium</option>
                  <option value="cinematic-wide">Cinematic Wide</option>
                  <option value="wallart-editorial">Wall Art Editorial</option>
                </select>
              </Field>

              <Field label="Brand Slug">
                <select
                  value={form.brand_slug}
                  onChange={(e) => updateField("brand_slug", e.target.value)}
                >
                  <option value="mayachitro">mayachitro</option>
                </select>
              </Field>

              <Field label="Theme Style">
                <select
                  value={form.theme_style || "dark"}
                  onChange={(e) => updateField("theme_style", e.target.value)}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="cream">Cream</option>
                </select>
              </Field>

              <Field label="Hero Size">
                <select
                  value={form.hero_size || "standard"}
                  onChange={(e) => updateField("hero_size", e.target.value)}
                >
                  <option value="standard">Standard</option>
                  <option value="compact">Compact</option>
                  <option value="large">Large</option>
                </select>
              </Field>

              <Field label="Sort Order">
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => updateField("sort_order", e.target.value)}
                />
              </Field>
            </div>
          </section>

          <section className="admin-hero-card preview-card">
            <h3>Quick Preview</h3>

            <div className="admin-hero-preview">
              <PreviewImage path={form.main_image_path} label="Main image" />

              <div>
                <p>{form.eyebrow_text || "Eyebrow text"}</p>
                <h4>{form.title_text || "Hero title"}</h4>
                <span>{form.subtitle_text || "Hero subtitle"}</span>
              </div>
            </div>
          </section>
        </div>

        <section className="admin-hero-card">
          <h3>Hero Copy</h3>

          <div className="admin-hero-grid">
            <Field label="Product Code">
              <input
                value={form.product_code || ""}
                onChange={(e) => updateField("product_code", e.target.value)}
                placeholder="MC-0008"
              />
            </Field>

            <Field label="Eyebrow Text">
              <input
                value={form.eyebrow_text || ""}
                onChange={(e) => updateField("eyebrow_text", e.target.value)}
                placeholder="Photography & Wall Art Marketplace"
              />
            </Field>
          </div>

          <Field label="Title Text">
            <textarea
              value={form.title_text || ""}
              onChange={(e) => updateField("title_text", e.target.value)}
              placeholder="Visual Stories & Printable Wall Art"
            />
          </Field>

          <Field label="Subtitle Text">
            <textarea
              value={form.subtitle_text || ""}
              onChange={(e) => updateField("subtitle_text", e.target.value)}
              placeholder="Short selling subtitle"
            />
          </Field>
        </section>

        <section className="admin-hero-card">
          <h3>Buttons</h3>

          <div className="admin-hero-grid">
            <Field label="Primary Button Text">
              <input
                value={form.primary_button_text || ""}
                onChange={(e) =>
                  updateField("primary_button_text", e.target.value)
                }
              />
            </Field>

            <Field label="Primary Button Link">
              <input
                value={form.primary_button_link || ""}
                onChange={(e) =>
                  updateField("primary_button_link", e.target.value)
                }
              />
            </Field>

            <Field label="Secondary Button Text">
              <input
                value={form.secondary_button_text || ""}
                onChange={(e) =>
                  updateField("secondary_button_text", e.target.value)
                }
              />
            </Field>

            <Field label="Secondary Button Link">
              <input
                value={form.secondary_button_link || ""}
                onChange={(e) =>
                  updateField("secondary_button_link", e.target.value)
                }
              />
            </Field>

            <Field label="Third Button Text">
              <input
                value={form.third_button_text || ""}
                onChange={(e) => updateField("third_button_text", e.target.value)}
              />
            </Field>

            <Field label="Third Button Link">
              <input
                value={form.third_button_link || ""}
                onChange={(e) => updateField("third_button_link", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="admin-hero-card">
          <h3>Main Image Control</h3>

          <Field
            label="Main Image Path"
            hint="Use public upload path, for example: /uploads/public/products/..."
          >
            <textarea
              value={form.main_image_path || ""}
              onChange={(e) => updateField("main_image_path", e.target.value)}
              placeholder="/uploads/public/products/..."
            />
          </Field>

          <div className="admin-hero-grid">
            <Field label="Main Image Fit">
              <select
                value={form.main_image_fit}
                onChange={(e) => updateField("main_image_fit", e.target.value)}
              >
                <option value="cover">cover — premium full image fill</option>
                <option value="contain">contain — show full mockup</option>
              </select>
            </Field>

            <Field
              label="Main Image Position"
              hint="Examples: center center, 58% center, left center, right center"
            >
              <input
                value={form.main_image_position || ""}
                onChange={(e) =>
                  updateField("main_image_position", e.target.value)
                }
                placeholder="center center"
              />
            </Field>

            <Field label="Main Image Scale">
              <input
                type="number"
                step="0.01"
                min="0.8"
                max="1.4"
                value={form.main_image_scale}
                onChange={(e) => updateField("main_image_scale", e.target.value)}
              />
            </Field>

            <Field label="Side Cards Count">
              <select
                value={form.side_cards_count}
                onChange={(e) => updateField("side_cards_count", e.target.value)}
              >
                <option value="0">0 side cards</option>
                <option value="1">1 side card</option>
                <option value="2">2 side cards</option>
                <option value="3">3 side cards</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="admin-hero-card">
          <h3>Caption & Visibility</h3>

          <div className="admin-hero-grid">
            <Field label="Caption Text">
              <input
                value={form.caption_text || ""}
                onChange={(e) => updateField("caption_text", e.target.value)}
                placeholder="MC-0008 · Featured Photography"
              />
            </Field>

            <Field label="Caption Mode">
              <select
                value={form.caption_mode || "compact"}
                onChange={(e) => updateField("caption_mode", e.target.value)}
              >
                <option value="hidden">Hidden</option>
                <option value="compact">Compact</option>
                <option value="wide">Wide</option>
              </select>
            </Field>
          </div>

          <div className="admin-hero-check-grid">
            <label>
              <input
                type="checkbox"
                checked={!!form.caption_enabled}
                onChange={(e) => updateField("caption_enabled", e.target.checked)}
              />
              Show caption
            </label>

            <label>
              <input
                type="checkbox"
                checked={!!form.trust_bar_enabled}
                onChange={(e) =>
                  updateField("trust_bar_enabled", e.target.checked)
                }
              />
              Show trust bar
            </label>

            <label>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => updateField("is_active", e.target.checked)}
              />
              Active slide
            </label>
          </div>
        </section>

        <section className="admin-hero-card">
          <h3>Side Images</h3>

          <div className="admin-side-image-grid">
            {[1, 2, 3].map((number) => (
              <article key={number}>
                <PreviewImage
                  path={form[`side_image_${number}`]}
                  label={`Side image ${number}`}
                />

                <Field label={`Side Image ${number} Path`}>
                  <textarea
                    value={form[`side_image_${number}`] || ""}
                    onChange={(e) =>
                      updateField(`side_image_${number}`, e.target.value)
                    }
                  />
                </Field>

                <Field label={`Side Image ${number} Title`}>
                  <input
                    value={form[`side_image_${number}_title`] || ""}
                    onChange={(e) =>
                      updateField(
                        `side_image_${number}_title`,
                        e.target.value
                      )
                    }
                  />
                </Field>
              </article>
            ))}
          </div>
        </section>

        <section className="admin-hero-card">
          <h3>Trust Tags & Timing</h3>

          <div className="admin-hero-grid">
            <Field label="Tag 1">
              <input
                value={form.tag_1 || ""}
                onChange={(e) => updateField("tag_1", e.target.value)}
              />
            </Field>

            <Field label="Tag 2">
              <input
                value={form.tag_2 || ""}
                onChange={(e) => updateField("tag_2", e.target.value)}
              />
            </Field>

            <Field label="Tag 3">
              <input
                value={form.tag_3 || ""}
                onChange={(e) => updateField("tag_3", e.target.value)}
              />
            </Field>

            <Field label="Auto Slide Seconds">
              <input
                type="number"
                min="3"
                value={form.auto_slide_seconds}
                onChange={(e) =>
                  updateField("auto_slide_seconds", e.target.value)
                }
              />
            </Field>
          </div>
        </section>
      </form>
    </main>
  );
}
